// https://github.com/opentypejs/opentype.js
import { Font, Path, load } from 'opentype.js';

// https://www.npmjs.com/package/svg-path-bbox
import svgPathBbox from 'svg-path-bbox';

// https://github.com/fontello/svgpath
import svgpath from 'svgpath';

// https://www.npmjs.com/package/point-in-svg-polygon
// @ts-ignore
import pointInSvgPolygon from 'point-in-svg-polygon';

import { IColor, IIcon, ILetter, ILetters } from '../interfaces';
import { getRandom, getRandomInt } from './math-provider';
import { TABLER_ICON_HEIGHT, TABLER_ICON_WIDTH } from './icons-provider';
import { createSVG, drawPath, drawRect, parseSVG, svgIconsToSymbols } from './svg-provider';
import { getColorId, getRGBAColor } from './color-provider';
import uniqueId from 'lodash-es/uniqueId';

// TODO: make params
const FONT_SIZE = 400;
const LETTER_SPACING = 50;
const ICON_PADDING = 2;
const ASPECT_RATIO = 10 / 220; // 10 icons per 220px width
const BOX_PADDING = 20;

let font:Font|null = null;

const isPointOnPath = (centerX: number, centerY: number, $path: SVGPathElement, $svg: SVGSVGElement, segments: any) => {

    // the APIs of the browser, that detect if point is inside SVG
    // are working only on rendered SVGs!!!
    // console.log(document.elementFromPoint(centerX, centerY))

    try{
        // this is an old API that works in all browsers
        const point = $svg.createSVGPoint();
        point.x = centerX;
        point.y = centerY;
        return $path.isPointInFill(point);
    }
    catch(ex){
        console.log(ex);
    }

    try{
        // this is a new API, that still doesn't work in all browsers
        const point = new DOMPoint(centerX, centerY);
        //path.isPointInStroke(point)
        return $path.isPointInFill(point);
    }
    catch(ex){
        console.log(ex);
    }

    try{
        // this is npm package, that works not so well, and is used like fallback
        return pointInSvgPolygon.isInside([centerX, centerY], segments);
    }
    catch(ex){
        console.log(ex);
    }

    return false;
};

/**
 * Get text letters in SVG format including all the required details like width / height.
 */
const getLetters = async (text: string) : Promise<ILetters> => {

    const $lettersSVG = createSVG(); // used to determine later, if the point is in the letter path
    const artLetters: ILetter[] = [];

    // load font if needed (only once)
    if(!font){
        font = await load('fonts/din-pro-black.otf');
    }

    const letters = text.toUpperCase().split('');

    let leftOffset = BOX_PADDING;
    let maxTextHeight = 0;
    let maxTextWidth = 0;

    for(let i=0; i<letters.length; i++){

        const letter = letters[i];
        if(letter === ' '){
            leftOffset += LETTER_SPACING;
            continue;
        }

        // convert letter to path
        const path:Path = font.getPath(letter, 0, 0, FONT_SIZE);

        // get letter svg D property
        let d = path.toPathData(2);

        // get letter path dimensions
        const [initialTextX, initialTextY, initialTextX1, initialTextY1] = svgPathBbox(d);

        let letterWidth = initialTextX1 - initialTextX;
        let letterHeight = initialTextY1 - initialTextY;

        d = svgpath(d)
            .translate(-initialTextX + leftOffset, -initialTextY + BOX_PADDING)
            .rel()
            .round(1)
            .toString();

        const [textX, textY, textX1, textY1] = svgPathBbox(d);
        letterWidth = textX1 - textX;
        letterHeight = textY1 - textY;

        maxTextHeight = Math.max(maxTextHeight, letterHeight);
        maxTextWidth += letterWidth;

        // prepare the next letter position (left offset)
        leftOffset += letterWidth + LETTER_SPACING;

        const $path = drawPath(0, 0, d, '#000');
        $lettersSVG.append($path);

        artLetters.push({
            d,
            letterWidth,
            letterHeight,
            textX,
            textY,
            segments: pointInSvgPolygon.segments(d),
            $path: $path,
        });
    }

    const textWidth = Math.max(0, maxTextWidth + (letters.length - 1) * LETTER_SPACING) + BOX_PADDING * 2;
    const textHeight = Math.max(0, maxTextHeight) + BOX_PADDING * 2;

    $lettersSVG.setAttribute('width', textWidth.toString());
    $lettersSVG.setAttribute('height', textHeight.toString());
    $lettersSVG.setAttribute('viewBox', `0 0 ${ textWidth } ${ textHeight }`);

    return {
        letters: artLetters,
        textWidth,
        textHeight,
        $lettersSVG,
    };
};

/**
 * Generate artistic text made of icons.
 */
export const getArtText = async (
    text: string,
    icons: IIcon[],
    colors: IColor[],
    backgroundColor: IColor,
    backgroundColorEnabled: boolean
) : Promise<string> => {
    if(icons.length === 0) return '';

    let iconsSVG = '';

    const symbols = svgIconsToSymbols(icons);
    const lettersData = await getLetters(text);
    // fixLettersAlignment(lettersData);

    // the APIs of the browser, that detect if point is inside of SVG
    // are working only on rendered SVGs!!!
    document.body.prepend(lettersData.$lettersSVG);

    for(let i=0; i<lettersData.letters.length; i++){
        const letter = lettersData.letters[i];

        const itemsPerWidth = Math.ceil(letter.letterWidth * ASPECT_RATIO);
        const itemsPerHeight = Math.ceil(letter.letterHeight * ASPECT_RATIO);

        // find cell dimensions
        const cellWidth = letter.letterWidth / itemsPerWidth;
        const cellHeight = letter.letterHeight / itemsPerHeight;

        for(let r = 0; r<itemsPerHeight; r++) {
            for (let c = 0; c < itemsPerWidth; c++) {

                // find the center of the cell
                const centerX = letter.textX + cellWidth * c + cellWidth / 2;
                const centerY = letter.textY + cellHeight * r + cellHeight / 2;

                // debug ---------
                // iconsSVG += `<circle cx="${ centerX }" cy="${ centerY }" r="3" fill="red" />`;

                // check if the center of the shape is inside the letter,
                // and ignore all points that are located outside the letter
                if(!isPointOnPath(centerX, centerY, letter.$path, lettersData.$lettersSVG, letter.segments)) continue;

                // the icon should be placed inside the cell
                const randomAngle = getRandom(0, 259, 2);

                const scaleX = cellWidth / (TABLER_ICON_WIDTH + ICON_PADDING);
                const scaleY = cellHeight / (TABLER_ICON_HEIGHT + ICON_PADDING);

                const cx = (TABLER_ICON_WIDTH / 2);
                const cy = (TABLER_ICON_HEIGHT / 2);
                const tx = -cx * (scaleX - 1);
                const ty = -cy * (scaleY - 1);

                const randomIconIndex = getRandomInt(0, symbols.length - 1);
                const symbol = symbols[randomIconIndex];

                const randomColorIndex = getRandomInt(0, colors.length - 1);
                const randomColor = colors.length > 0 ? colors[randomColorIndex] : { r: 120, g: 120 , b: 120, a: 1, cid: uniqueId() };

                // transformations on symbols don't work in Safari!!!
                // Safari is a new IE 6 :((
                // <!--<use href="#${ symbol.id }" />-->
                const icon = `<g    class="art-text-g color-${ getColorId(randomColor) }"
                                stroke="rgba(${ randomColor.r }, ${ randomColor.g }, ${ randomColor.b }, ${ randomColor.a })"
                                transform="
                                rotate(${ randomAngle } ${ centerX } ${ centerY }) 
                                translate(${ letter.textX + cellWidth * c + tx } ${ letter.textY + cellHeight * r + ty })
                                scale(${ scaleX } ${ scaleY })">
                    ${ symbol.$symbol.innerHTML }
            </g>`;
                iconsSVG += icon;

            }
        }
    }

    lettersData.$lettersSVG.remove();

    // removed:
    // ${ symbols.map(symbol => symbol.$symbol.outerHTML).join('') }

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg"
     width="${ lettersData.textWidth }"
     height="${ lettersData.textHeight }"
     viewBox="0 0 ${ lettersData.textWidth } ${ lettersData.textHeight }"
     stroke-width="2"
     stroke="currentColor"
     fill="none"
     stroke-linecap="round"
     stroke-linejoin="round">
     ${ backgroundColorEnabled ? `<rect 
                                        class="art-text-bg-rect" 
                                        x="0" 
                                        y="0" 
                                        fill="${ getRGBAColor(backgroundColor) }" 
                                        stroke="none" 
                                        width="${ lettersData.textWidth }" 
                                        height="${ lettersData.textHeight }" 
                                   />` : '' }
    ${ iconsSVG }
</svg>    
    `;
};

/**
 * update specific color
 */
export const updateArtTextColors = (artTextSVG: string, oldColorId: string, newColor: IColor) => {

    const $svg = parseSVG(artTextSVG);
    const $gList = $svg.querySelectorAll(`.art-text-g.color-${ oldColorId }`);

    for(let $g of $gList) {
        $g.setAttribute('stroke', `rgba(${ newColor.r }, ${ newColor.g }, ${ newColor.b }, ${ newColor.a })`);
    }

    return (new XMLSerializer()).serializeToString($svg);
};

export const updateArtTextBackground = (artTextSVG: string, bgColor: IColor, enable: boolean) => {
    const $svg = parseSVG(artTextSVG);
    const $rect = $svg.querySelector('.art-text-bg-rect');

    if(enable){
        if($rect){
            // only change rect color
            $rect.setAttribute('fill', getRGBAColor(bgColor));
        }
        else{
            // create the rect and set the color
            const $rect = drawRect(
                0, 0, '100%', '100%', getRGBAColor(bgColor),
                '', 0, 0, 0, 'art-text-bg-rect'
            );
            $svg.prepend($rect);
        }
    }
    else{
        if($rect){
            // remove the rect
            $rect.remove();
        }
        else{
            // do nothing
        }
    }

    return (new XMLSerializer()).serializeToString($svg);
};

/*
const fixLettersAlignment = (lettersData: ILetters) => {
    for(let i=0; i<lettersData.letters.length; i++) {
        const letter = lettersData.letters[i];
        const offsetHeight = Math.max(0, lettersData.textHeight - letter.letterHeight);
        if(offsetHeight <= 0) continue;

        letter.d = svgpath(letter.d)
            .translate(0, offsetHeight)
            .rel()
            .round(1)
            .toString();

        letter.$path.setAttribute('d', letter.d);
        letter.segments = pointInSvgPolygon.segments(letter.d);

        const [textX, textY, textX1, textY1] = svgPathBbox(letter.d);
        letter.letterWidth = textX1 - textX;
        letter.letterHeight = textY1 - textY;
        letter.textX = textX;
        letter.textY = textY;
    }
};*/
